import { useFormik } from "formik";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLeavePageConfirmation } from "../useLeavePageConfirmation";

export default function Footer(props: {
  items: any;
}) {

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const recaptchaRef = useRef<any>(null);
  const [isSubmited, setIsSubmited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formChanged, setFormChanged] = useState(false); // State to track form changes
  const initialValues = {
    company: "",
    email: "",
    message: "",
  };

  const validate = (values: any) => {
    setLoading(true);
    let errors: any = {};
    if (!values.company) {
      errors.company =
        i18n.language === "en"
          ? "company name is required"
          : "Le nom d'Entreprise est obligatoire";
    }

    if (!values.message) {
      errors.message =
        i18n.language === "en"
          ? "Message is required"
          : "Le message est obligatoire";
    }
    if (!values.email) {
      errors.email =
        i18n.language === "en"
          ? "Email is required"
          : "L'email est obligatoire";
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      errors.email =
        i18n.language === "en"
          ? "Invalid email address"
          : "Adresse e-mail invalide";
    }

    setLoading(false);
    return errors;
  };
  const handleSubmit = (values: any) => {
    setLoading(true);
    dispatch.mailModel
      .sendEmail({
        email: formik.values.email,
        company: formik.values.company,
        message: formik.values.message,
      })
      .then((res: any) => {
        recaptchaRef.current.execute();
        setLoading(false);
        setIsSubmited(true);
        setFormChanged(false);
        formik.resetForm();
        // Hide the success message after 10 seconds
        setTimeout(() => {
          setIsSubmited(false);
        }, 10000); // 10000 milliseconds = 10 seconds
      });
  };
  const onReCAPTCHAChange = (captchaCode: any) => {
    if (!captchaCode) {
      return;
    }
    recaptchaRef.current.reset();
  };
  const formik = useFormik({
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validate,
    onSubmit: handleSubmit,
    // Set formChanged state to true when form values change
  });
  useEffect(() => {
    if (formik.dirty) {
      setFormChanged(true);
    } else {
      setFormChanged(false);
    }
  }, [formik.dirty]);
  useLeavePageConfirmation(
    formChanged,
    i18n.language === "en"
      ? "You have unsaved changes. Are you sure you want to leave?"
      : "Vous avez des changements non sauvés. Êtes-vous sûr de vouloir partir ?"
  );
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (formChanged) {
        // Display warning message
        event.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Remove event listener on component unmount
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [formChanged]);

  // Function to reset formChanged state when the form is submitted
  const handleFormSubmit = () => {
    setFormChanged(false);
    formik.handleSubmit();
  };
  const router = useRouter();

  const currentRouteD = router.asPath;

  const pathname =
    currentRouteD.replaceAll("/", "") === "imagine-en" ||
    currentRouteD.replaceAll("/", "") === "imagine-fr";

  const getItemElementText = (element: any) => {
    if(!element) return '';

    // Get title from translation
    if(element.translation != null && typeof element.translation == 'object' && element.translation instanceof Array && element.translation.length > 0) {
        // Get matching locale translation
        for(const translation of element.translation) {
          if(translation.iSOCode != i18n.language || !translation.title) continue;

          return translation.title;
        }

        return element.translation[0].title;
    }

    return element.title ?? '';
  };

  const getItemElementTextByIndex = (item: any, index: number) => {
    if(!item.elements || typeof item.elements != 'object' || !(item.elements instanceof Array)
      || item.elements.length <= index) return '';

    return getItemElementText(item.elements[index]);
  };

  const getItemByType = (type: string) => {
    if(props.items != null && props.items.length > 0) {
      for(const item of props.items) {
        if(item.type != type) continue;
        return item;
      }
    }

    return null;
  };

  const descriptionItem = useMemo(() => {
    return getItemByType('description');
  }, [props.items]);

  const addressItem = useMemo(() => {
    return getItemByType('address');
  }, [props.items]);

  const phoneItem = useMemo(() => {
    return getItemByType('phone');
  }, [props.items]);

  const handlePlacesClick = () => {
    const placesItem = getItemByType('places');
    if(!placesItem || !placesItem.title) return;

    window.open(placesItem.title_fr, '_blank');
  };

  return (
    <footer>
      <div className="top-footer">
        <div className="container">
          <div className="row justify-content-between gap-4 gap-sm-0">
            <div className="col-10 m-auto">
              <div className="logo-description">
                <img src="/images/mas/logo.svg" alt="logo.svg" />
                <p className="text-start">
                  {
                    descriptionItem != null ? getItemElementTextByIndex(descriptionItem, 0) :
                      "9 hectares en bio (certification Ecocert), à Arzens dans l'Aude."
                  }
                </p>
              </div>
              <div className="row">
                <div className="col-8">
                  <div className="address-call">
                    <div className="address">
                      <a href="#" className="d-flex">
                        <div className="icon">
                          <i className="fa-solid fa-location-dot me-4"></i>
                        </div>
                        {
                          addressItem != null && (
                            addressItem.elements.map((element: any, index: number, arr: any[]) => (
                              <>
                                { getItemElementText(element) }
                                { (index < (arr.length-1)) &&  (<br />) }
                              </>
                            ))
                          )
                        }
                        {
                          addressItem == null && (
                            <>
                              Le Mas de mon Père <br />
                              18 chemin du roudel <br />
                              11290 ARZENS
                            </>
                          )
                        }
                      </a>
                    </div>
                    <div className="call">
                      <a 
                        href={`${phoneItem != null ? `tel:${getItemElementTextByIndex(phoneItem, 0)}` : "tel:+33683481273"}`} 
                        className="d-flex"
                      >
                        <div className="icon">
                          <i className="fa-solid fa-phone-volume me-4"></i>
                        </div>
                        {
                          phoneItem != null && phoneItem.elements.length > 1 &&
                            phoneItem.elements.slice(1).map((element: any, index: number, arr: any[]) => (
                              <>
                                { getItemElementText(element) }
                                { (index < (arr.length-1)) &&  (<br />) }
                              </>
                            ))
                        }
                        {
                          phoneItem == null && (
                            <>
                              Frédéric Palacios <br />
                              06.83.48.12.73
                            </>
                          )
                        }
                      </a>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-4">
                  <div className="newsletter-social-icon">
                    <h6>Où nous retrouver ?</h6>
                    <p className="text-start">
                      Découvrez les lieux où nos vins sont disponibles.
                    </p>
                    <div className="custom-footer">
                      <div className="d-flex align-items-baseline">
                        <p>
                          Consultez la liste complète <span onClick={handlePlacesClick}>ici</span>
                        </p>
                        <button type="button" onClick={handlePlacesClick}>
                          <i className="fa-solid fa-circle-arrow-right"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="copy-right-link">
        <div className="container">
          <div className="row text-center">
            <p>© 2025, Tous droits réservés. Design by MENAPS.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
